import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useRequestData, useLabels } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { setClassNames } from '@ddc/classnames-js';
import ClearFilters from './ClearFilters';
import FilterItem from './FilterItem';
import FilterItemList from './FilterItemList';
import VehicleCount from './VehicleCount';
import InventorySort from './InventorySort';
import { trapFocus } from '../utilities/trapFocus';

// Cross-widget pubsub topics. The sibling ws-inv-facets widget publishes
// these when the user enters/leaves a nested facet detail panel; we
// suppress this widget's modal chrome (title + close icon) while a nested
// panel is open so the two widgets don't render competing chrome.
// Contract documented in the sibling repo:
// ws-inv-facets/src/components/nested/pubsub-events.js (story 1-3 / PR #178).
// String values must remain byte-equivalent with the sibling's exports.

/**
 * Subscribed when the user enters a nested facet detail panel in the
 * sibling ws-inv-facets widget. Triggers chrome suppression on this widget.
 * @event ws-inv-facets/nested-panel-open
 * @payload { facetId: string } — the active facet's id (or groupId for
 *   multi-facet groups). Currently unused on this side but documented for
 *   parity with the emitter contract.
 */
const NESTED_PANEL_OPEN = 'ws-inv-facets/nested-panel-open';

/**
 * Subscribed when the user navigates back to the nested initial list,
 * dismisses via the sibling's mobile X close icon, or presses Escape inside
 * a sibling detail panel. Triggers chrome restoration.
 * @event ws-inv-facets/nested-panel-closed
 * @payload undefined — no payload; the event is purely a state signal.
 */
const NESTED_PANEL_CLOSED = 'ws-inv-facets/nested-panel-closed';

const FilterModalButton = ({ count, appliedFilters, sortOptions }) => {
	const [showModal, setModalShow] = useState(false);
	const [animationName, setAnimationName] = useState(null);
	const [nestedPanelOpen, setNestedPanelOpen] = useState(false);
	const { widgetName, windowId } = useRequestData();
	const labels = useLabels();
	const filtersHeading = labels.get('FILTERS');
	// Use different label based on whether sort options are included (OLD UI vs NEW UI)
	const isRenderSortDropdown = sortOptions && sortOptions.length > 0;
	const buttonLabel = isRenderSortDropdown
		? labels.get('FILTER_SLASH_SORT')
		: labels.get('FILTER_NO_DOTS');
	const closeFiltersLabel = labels.get('CLOSE_FILTERS');
	const initialFocusRef = useRef(null);
	// define feature flag for scroll to top listing on filter

	const hideModal = (scrollToListings = true) => {
		// Remove animation className and allow animation transition before removing modal
		setAnimationName(null);
		setTimeout(() => {
			setModalShow(false);
			if (scrollToListings) {
				// scroll to top of listings on mobile facets after submitting
				window.DDC.scrollToAnchor(
					$('[data-widget-id="inventory-filters1"]'),
					{
						heightAdj: $('.header-minimal-container').height()
					}
				);
			}
		}, 300);
	};

	const onShowModal = () => {
		setModalShow(true);
		window.dispatchEvent(
			new CustomEvent('wiapiPlaceholderAvailable', {
				detail: {
					location: 'search-facet-pricing'
				}
			})
		);
	};

	const supportARIA = (modal) => {
		const modalWrap = modal.closest('[aria-hidden]');
		if (modalWrap) {
			modalWrap?.removeAttribute('aria-hidden');

			// Listen for modal in modal
			window?.DDC?.pubsub?.subscribe('wsm-common-modal/closed', () => {
				modalWrap?.removeAttribute('aria-hidden');
			});
		}
	};

	const onEntered = () => {
		const modal = document.querySelector('.modal.dock-full');
		if (modal) {
			initialFocusRef?.current?.focus();
		}
	};

	// Subscribe to cross-widget pubsub topics on mount; unsubscribe on
	// unmount via the bound { unsubscribe } each subscribe call returns
	// (ddc-pubsub src/pubsub.js:62).
	useEffect(() => {
		if (!window?.DDC?.pubsub?.subscribe) {
			return undefined;
		}
		const subs = [
			window.DDC.pubsub.subscribe(
				'ws-inv-filters/close-filters',
				(scrollToListings) => hideModal(scrollToListings)
			),
			window.DDC.pubsub.subscribe(NESTED_PANEL_OPEN, () => {
				setNestedPanelOpen(true);
			}),
			window.DDC.pubsub.subscribe(NESTED_PANEL_CLOSED, () => {
				setNestedPanelOpen(false);
			})
		];
		return () => {
			subs.forEach((sub) => sub?.unsubscribe?.());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks invoke stable setters only; pinned to mount
	}, []);

	useEffect(() => {
		if (showModal) {
			window.DDC?.InvData?.publishEvent?.('ws-inv-filters/modal-show');

			let deActivateFocusTrap = null;

			// Add animation className after modal element is found
			const updateModal = () => {
				const modal = document.querySelector('.modal.dock-full');
				if (modal) {
					supportARIA(modal);
					setTimeout(() => setAnimationName('slide'), 50);
					// eslint-disable-next-line no-use-before-define
					clearInterval(intervalModal);
					deActivateFocusTrap = trapFocus(modal);
				}
			};
			const intervalModal = setInterval(updateModal, 50);

			trackEvent(widgetName, windowId, {
				element: isRenderSortDropdown
					? 'Filter/sort button'
					: 'Filter button',
				elementCTA: isRenderSortDropdown ? 'Filter/Sort' : 'Filter',
				result: 'Filter panel displayed'
			});

			return () => {
				clearInterval();
				if (deActivateFocusTrap) {
					deActivateFocusTrap();
				}
			};
		} else {
			window.DDC?.InvData?.publishEvent?.('ws-inv-filters/modal-hide');
		}

		return () => {};
	}, [showModal, isRenderSortDropdown, widgetName, windowId]);

	// Chrome (modal title + close button + chip row) is hidden when a
	// nested facet panel is open. ws-inv-facets only emits
	// nested-panel-open in nested mode, so accordion mode never triggers
	// suppression (AC #11: chrome renders normally when no events
	// arrive). Default state nestedPanelOpen=false guarantees no flash
	// on mount before the emitter's first publish.
	const showModalChrome = !nestedPanelOpen;

	return (
		<React.Fragment>
			<Modal
				show={showModal}
				onHide={hideModal}
				onEntered={onEntered}
				// dock-left slides the panel in from the left to match the
				// Filter button position (cms-web ddc-design-system _modals.scss).
				// Keep dock-full: it sets width and is the selector used above.
				className={setClassNames([
					'dock-full',
					'dock-left',
					animationName
				])}
				aria-modal="true"
				aria-labelledby="ws-inv-filters-modal-label"
			>
				<Modal.Body>
					{showModalChrome && (
						<div className="ddc-font-size-xlarge d-flex align-items-center ws-inv-filters-modal-header">
							<Modal.Title
								id="ws-inv-filters-modal-label"
								className="ddc-font-size-xlarge d-flex align-items-center"
							>
								<i
									className="ddc-icon ddc-icon-filter-list-controls mr-2"
									aria-hidden="true"
								/>
								<span>{filtersHeading}</span>
							</Modal.Title>
							<button
								type="button"
								id="hide-filters-modal-button"
								className="btn btn-unstyled ml-auto d-inline-flex align-items-center justify-content-center p-3 ws-inv-filters-close-button"
								onClick={hideModal}
								aria-label={closeFiltersLabel}
								ref={initialFocusRef}
							>
								<i
									className="ddc-icon ddc-icon-remove2 ddc-icon-size-large"
									aria-hidden="true"
								/>
							</button>
						</div>
					)}
					{showModalChrome && !!appliedFilters.length && (
						<React.Fragment>
							<FilterItemList minBreakpoint="xs">
								{appliedFilters.map((filter) =>
									filter.values.map((item) => (
										<FilterItem
											{...item}
											key={[filter.id, item.value].join(
												'-'
											)}
											filterId={filter.id}
											filterLabel={filter.label}
										/>
									))
								)}
								<ClearFilters
									label={labels.get('CLEAR_FILTERS')}
								/>
							</FilterItemList>
							<hr className="modal-divider" />
						</React.Fragment>
					)}
					{/* Conditionally render sort dropdown for OLD UI (when sortOptions provided) */}
					{isRenderSortDropdown && (
						<>
							<InventorySort {...{ sortOptions }} />
							<hr className="modal-divider mb-0" />
						</>
					)}
					<div className="modal-filters-react-portal" />
				</Modal.Body>
				<Modal.Footer className="top-gradient border-0">
					<VehicleCount
						count={count}
						displayStyle="modalButton"
						setModalShow={setModalShow}
						hasFilters={!!appliedFilters.length}
					/>
				</Modal.Footer>
			</Modal>
			<button
				type="button"
				id="show-filters-modal-button"
				className={setClassNames([
					'btn',
					'btn-default',
					'font-weight-bold',
					isRenderSortDropdown
						? 'btn-block ddc-font-size-large'
						: 'text-nowrap d-inline-block mr-4'
				])}
				onClick={onShowModal}
			>
				<i
					className="ddc-icon ddc-icon-filter-list-controls"
					aria-hidden="true"
				/>{' '}
				{buttonLabel}{' '}
				{appliedFilters.length > 0 && (
					<div className="badge mb-0">{appliedFilters.length}</div>
				)}
			</button>
		</React.Fragment>
	);
};

FilterModalButton.propTypes = {
	count: PropTypes.number.isRequired,
	appliedFilters: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			label: PropTypes.string,
			values: PropTypes.arrayOf(
				PropTypes.shape({
					value: PropTypes.string,
					label: PropTypes.string
				})
			)
		})
	),
	sortOptions: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string,
			label: PropTypes.string
		})
	)
};

FilterModalButton.defaultProps = {
	appliedFilters: [],
	sortOptions: []
};

export default FilterModalButton;
